import { useEffect, useState } from "react";

import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { Check, Loader, Lock, Trash2 } from "lucide-react";

import {
  decryptPayload,
  getCategoryDisplayLabel,
  getObjLength,
  parseLocalStorageJson,
  normalizeSubscriptionCategory,
} from "../../helpers/utils";
import { isEldoradoSiteSignup } from "../../helpers/eldoradoSignup";
import VerificationMethod from "./VerificationMethod";
import { Loader as LoaderComponent } from "../../common/loader";
import { Button } from "../../common/customDesignedComponents/button";
import { propertiesAction } from "../../../redux/slices/properties/propertiesSlice";

interface OwnerVerificationV2Props {
  propertiesList?: any;
  propertyAction?: (event: any, type: string, data: any) => void;
  addedNewProperty?: () => void;
  isInvestor?: boolean;
  goToSubscriptions?: () => void;
  closeBtnFunction?: () => void;
  verified?: any;
  finishBtn?: () => void;
  addNewProperty?: () => void;
  onBtnClick?: (e: any, action: string) => void;
  setPropertiesData?: () => void;
  getMailingAddress?: () => void;
  isAdminFlow?: boolean;
  onAdminNext?: () => void;
  onAddAnotherProperty?: () => void;
  hideAddAnotherProperty?: boolean;
  isAssistant?: boolean;
  isAdmin?: boolean;
}

const OwnerVerificationV2 = ({
  propertiesList,
  propertyAction,
  addedNewProperty,
  isInvestor,
  goToSubscriptions,
  closeBtnFunction,
  verified,
  finishBtn,
  addNewProperty,
  onBtnClick,
  setPropertiesData,
  getMailingAddress,
  isAdminFlow = false,
  onAdminNext,
  onAddAnotherProperty,
  hideAddAnotherProperty = false,
  isAssistant = false,
  isAdmin = false,
}: OwnerVerificationV2Props) => {
  const dispatch = useDispatch();
  const [showMailVerification, setShowMailVerification] = useState(false);
  const [checkHasNoProperties, setCheckHasNoProperties] = useState(true);
  const category =
    decryptPayload(localStorage.getItem("category")) ||
    localStorage.getItem("signup_product_tier") ||
    "complete_protection";
  const IsCompleteProtection = category === "complete_protection";
  const isBasicMonitoring = category === "basic_monitoring";
  const isEldoradoSignup = isEldoradoSiteSignup();
  const isCountyUser =
    Boolean(parseLocalStorageJson("county_user_id")) || isEldoradoSignup;

  const getPropertyCategoryLabel = (property: any) => {
    const propertyCategory = normalizeSubscriptionCategory(property);
    return getCategoryDisplayLabel(propertyCategory) || propertyCategory || "-";
  };
  // Filter out properties that are not added (same logic as ownerVerified.js line 357)
  const filteredProperties = propertiesList?.success
    ? propertiesList?.data?.filter((property: any) => !property?.not_added)
    : [];

  const handleDeleteProperty = (event: any, property: any) => {
    if (propertyAction) {
      propertyAction(event, "delete", property);
    }
  };

  // Check if there are no properties
  const hasNoProperties = propertiesList?.data?.length === 0;

  // ID verification (or phone consent) already verified every mailing address,
  // so no postcard goes out and the postcard screen would only confuse.
  const postcardNotNeeded =
    filteredProperties.length > 0 &&
    filteredProperties.every(
      (property: any) => property?.mailing_address_verified === true,
    );

  // Determine the Next button handler (line 549-577 logic)
  const handleOwnerVerificationNextStep = () => {
    if (hasNoProperties) return; // Don't proceed if no properties
    // County Investor page payment: the account exists, so go straight to the plan.
    if (isInvestor) {
      handleSelect();
      return;
    }
    if (isAdminFlow && onAdminNext) {
      onAdminNext();
      return;
    }
    if (IsCompleteProtection && postcardNotNeeded) {
      handleSelect();
    } else if (IsCompleteProtection) {
      setShowMailVerification(true);
    } else {
      setPropertiesData?.();
      if (!isBasicMonitoring) {
        getMailingAddress?.();
      }
      onBtnClick?.(null, "owner-verification");
    }
  };

  const handleAddAnotherProperty = () => {
    dispatch(propertiesAction.showSecurityInfoScreen(false));
    if (onAddAnotherProperty) {
      onAddAnotherProperty();
      return;
    }
    addedNewProperty?.();
  };

  const handleSelect = () => {
    // Navigate to credit card page
    if (isInvestor && goToSubscriptions) {
      goToSubscriptions();
    } else if (finishBtn) {
      finishBtn();
    }
  };

  const handleBack = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setShowMailVerification(false);
  };

  useEffect(() => {
    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if ((isAdminFlow || isInvestor) && propertiesList?.success) {
      setCheckHasNoProperties(false);
    }
  }, [isAdminFlow, isInvestor, propertiesList]);

  useEffect(() => {
    if (isAdminFlow || isInvestor) return;
    if (!getObjLength(propertiesList)) return;

    const timeoutId = setTimeout(() => {
      if (propertiesList?.data?.length === 0 && !isEldoradoSiteSignup()) {
        addNewProperty?.();
      }
      setCheckHasNoProperties(false);
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [propertiesList, addNewProperty, isAdminFlow, isInvestor]);

  // If checkHasNoProperties is still true after 10 seconds, call addNewProperty automatically
  useEffect(() => {
    if (isAdminFlow || isInvestor || !checkHasNoProperties) return;

    const fallbackId = setTimeout(() => {
      setCheckHasNoProperties(false);
      if (!isEldoradoSiteSignup()) {
        addNewProperty?.();
      }
    }, 10000);

    return () => clearTimeout(fallbackId);
  }, [checkHasNoProperties, addNewProperty, isAdminFlow, isInvestor]);

  return (
    <>
      {showMailVerification ? (
        <VerificationMethod
          handleSelect={handleSelect}
          handleBack={handleBack}
        />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto"
        >
          <div
            className={`bg-card border border-border shadow-lg overflow-hidden ${!isAdmin && "rounded-2xl"}`}
          >
            {/* Secure Connection Bar */}
            {!isAdmin && !isInvestor && (
              <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                <Lock className="h-4 w-4 text-white" />
                <span className="text-white font-medium text-sm">
                  Secure connection
                </span>
              </div>
            )}

            <div className="p-8 md:p-12">
              <h1 className="text-2xl md:text-3xl font-display font-semibold text-foreground text-center mb-2">
                {!IsCompleteProtection ? "Your Property" : "Owner Verified"}
              </h1>
              <p className="text-muted-foreground text-center text-sm mb-10">
                {!IsCompleteProtection
                  ? null
                  : `Your Ownership of the below property has been verified by
                  EquityProtect.`}
              </p>

              {/* Property Cards Grid - Skeleton when checking, real cards when ready */}
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                {checkHasNoProperties ? (
                  <>
                    {[1, 2].map((i) => (
                      <div
                        key={i}
                        className="relative bg-background rounded-xl border border-border p-6 shadow-sm animate-pulse"
                      >
                        <div className="h-8 w-8 rounded-full bg-muted -top-3 -left-3 absolute" />
                        <div className="mt-2 space-y-4">
                          <div className="h-5 bg-muted rounded w-3/4" />
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <div className="h-4 bg-muted rounded w-14" />
                              <div className="h-4 bg-muted rounded flex-1" />
                            </div>
                            <div className="flex gap-2">
                              <div className="h-4 bg-muted rounded w-14" />
                              <div className="h-4 bg-muted rounded flex-1" />
                            </div>
                            <div className="flex gap-2">
                              <div className="h-4 bg-muted rounded w-14" />
                              <div className="h-4 bg-muted rounded flex-1" />
                            </div>
                            <div className="flex gap-2">
                              <div className="h-4 bg-muted rounded w-14" />
                              <div className="h-4 bg-muted rounded flex-1" />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end pt-4">
                          <div className="h-9 bg-muted rounded w-24" />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  filteredProperties.map((property: any, index: number) => (
                    <motion.div
                      key={property?.property_id || property?.id || index}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className="relative bg-background rounded-xl border border-border p-6 shadow-sm"
                    >
                      {/* Green Checkmark Badge - Only show when verified (line 451) */}
                      {property?.is_verified === 1 && (
                        <div className="absolute -top-3 -left-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shadow-md">
                            <Check className="h-5 w-5 text-white" />
                          </div>
                        </div>
                      )}

                      <div
                        className={property?.is_verified === 1 ? "mt-2" : ""}
                      >
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <h3 className="font-semibold text-foreground">
                            Property: {property?.address || "-"}
                          </h3>
                          {isCountyUser && (
                            <span className="inline-flex shrink-0 items-center rounded-full border border-secondary/30 bg-secondary/10 px-2.5 py-0.5 text-xs font-semibold text-secondary">
                              {getPropertyCategoryLabel(property)}
                            </span>
                          )}
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex">
                            <span className="font-semibold text-foreground w-20">
                              City:
                            </span>
                            <span className="text-foreground">
                              {property?.city || "-"}
                            </span>
                          </div>
                          <div className="flex">
                            <span className="font-semibold text-foreground w-20">
                              State:
                            </span>
                            <span className="text-foreground">
                              {property?.state || "-"}
                            </span>
                          </div>
                          <div className="flex">
                            <span className="font-semibold text-foreground w-20">
                              Country:
                            </span>
                            <span className="text-foreground">
                              {property?.country || "USA"}
                            </span>
                          </div>
                          <div className="flex">
                            <span className="font-semibold text-foreground w-20">
                              Zip:
                            </span>
                            <span className="text-foreground">
                              {property?.zip || "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons - Conditional layout based on is_verified (line 386-390) */}
                      <div
                        className={`flex items-center pt-4 ${
                          property?.is_verified === 0
                            ? "justify-between"
                            : "justify-end"
                        }`}
                      >
                        {property?.is_verified === 0 && isAdminFlow ? (
                          <Button
                            onClick={(e) =>
                              propertyAction?.(e, "verify", property)
                            }
                            className="px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            Verify
                          </Button>
                        ) : property?.is_verified === 0 ? (
                          <Button
                            disabled
                            className="px-4 bg-[#d5ddd8] hover:bg-[#d5ddd8] text-[#9ea4a0] cursor-not-allowed"
                            title={
                              property?.from_investor
                                ? "Click to verify the Property or It'll be done by admin"
                                : "Property verification will be completed by admin"
                            }
                          >
                            QUESTIONS SKIPPED
                          </Button>
                        ) : null}

                        {/* Delete Button - Only show when there are multiple properties (line 430) */}
                        {propertiesList?.data?.length > 1 && !isAssistant && (
                          <button
                            onClick={(e) => handleDeleteProperty(e, property)}
                            className="text-red-500 hover:text-red-600 transition-colors"
                            aria-label="Delete property"
                            title="Delete this property"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {/* {!isEldoradoSiteSignup() ? ( */}
                {/* Still offered with no property left, so the agent is not stuck. */}
                {!isInvestor &&
                  (!hideAddAnotherProperty || hasNoProperties) && (
                    <Button
                      onClick={handleAddAnotherProperty}
                      variant="outline"
                      className="px-8 py-3 rounded-full font-semibold text-sm w-full sm:w-auto border-secondary text-secondary hover:bg-secondary/10"
                    >
                      ADD ANOTHER PROPERTY
                    </Button>
                  )}
                {/* ) : null} */}

                <Button
                  onClick={handleOwnerVerificationNextStep}
                  disabled={hasNoProperties}
                  className={`bg-secondary hover:bg-secondary/90 text-white font-semibold px-12 py-3 text-sm rounded-full shadow-md hover:shadow-lg transition-all w-full sm:w-auto ${
                    hasNoProperties ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  NEXT
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      <LoaderComponent open={checkHasNoProperties} />
    </>
  );
};

export default OwnerVerificationV2;
